import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { PipelineCard as PipelineCardType, ProjectStage } from '@1130-studio/shared'
import { PipelineCard } from './PipelineCard'
import { cn } from '../../lib/utils'

interface PipelineColumnProps {
  stage: ProjectStage
  cards: PipelineCardType[]
  onCardClick?: (card: PipelineCardType) => void
  onAddClick?: (stageId: string) => void
}

const stageColorClasses: Record<string, string> = {
  // Lead stages
  inbox: 'bg-accent-orange',
  qualifying: 'bg-yellow-500',
  estimation: 'bg-yellow-400',
  // Project stages
  backlog: 'bg-[#6b7280]',
  discussion: 'bg-accent-orange',
  design: 'bg-accent-violet',
  development: 'bg-accent-blue',
  review: 'bg-accent-pink',
  testing: 'bg-accent-emerald',
  released: 'bg-accent-green',
  // Archive
  lost: 'bg-red-500',
}

const stageTypeLabels: Record<string, string> = {
  lead: 'Заявки',
  project: 'Проекты',
}

export function PipelineColumn({ stage, cards, onCardClick, onAddClick }: PipelineColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
  })

  const colorClass = stageColorClasses[stage.slug] || 'bg-text-tertiary'

  // Don't render archive stages in normal column layout
  if (stage.type === 'archive') {
    return null
  }

  // Check if this is the first stage of a type section
  const isFirstLead = stage.slug === 'inbox'
  const isFirstProject = stage.slug === 'backlog'

  return (
    <div className="flex flex-col">
      {/* Section Header (only for first stage of each type) */}
      {(isFirstLead || isFirstProject) && (
        <div className="mb-4 px-4">
          <span className="text-xs font-bold uppercase tracking-widest text-text-tertiary">
            {stageTypeLabels[stage.type]}
          </span>
        </div>
      )}

      <div className="w-80 flex-shrink-0 flex flex-col">
        {/* Column Header */}
        <div className="flex items-center gap-3 px-4 py-3 mb-4">
          <div className={cn('w-3 h-3 rounded-full', colorClass)} />
          <span className="text-sm font-semibold uppercase tracking-wider text-text-secondary">
            {stage.name}
          </span>
          <span className="ml-auto w-6 h-6 bg-white/10 border border-white/10 rounded-full flex items-center justify-center text-xs font-semibold text-text-tertiary">
            {cards.length}
          </span>
          {onAddClick && stage.type === 'lead' && (
            <button
              onClick={() => onAddClick(stage.id)}
              className="w-7 h-7 bg-transparent border border-dashed border-white/20 rounded-md flex items-center justify-center text-text-tertiary hover:border-white hover:text-white hover:bg-white/10 transition-all"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          )}
        </div>

        {/* Column Cards */}
        <div
          ref={setNodeRef}
          className={cn(
            'flex flex-col gap-3 flex-1 min-h-[200px] transition-colors duration-200 rounded-xl p-1',
            isOver && 'bg-white/5'
          )}
        >
          <SortableContext items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {cards.length > 0 ? (
              cards.map((card) => (
                <PipelineCard
                  key={card.id}
                  card={card}
                  onClick={() => onCardClick?.(card)}
                />
              ))
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-10 border-2 border-dashed border-white/10 rounded-xl text-text-tertiary text-center">
                <svg
                  className="w-10 h-10 opacity-30 mb-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <p className="text-[13px]">
                  Перетащите карточку
                  <br />
                  сюда
                </p>
              </div>
            )}
          </SortableContext>
        </div>
      </div>
    </div>
  )
}
